import { Component, type ErrorInfo, type ReactNode } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import { Sidebar } from "./components/Sidebar";
import { ToastHost } from "./components/ToastHost";
import { TopBar } from "./components/TopBar";
import { featureRoutes } from "./features/registry";
import { useStore } from "./lib/store";
import { Admin } from "./pages/Admin";
import { CallSetup } from "./pages/CallSetup";
import { Console } from "./pages/Console";
import { History } from "./pages/History";
import { Stats } from "./pages/Stats";
import { Login } from "./pages/Login";
import { Logs } from "./pages/Logs";
import { Settings } from "./pages/Settings";
import { Setup } from "./pages/Setup";
import { Sip } from "./pages/Sip";

class PageCrash extends Component<{ children: ReactNode }, { message: string | null }> {
  state: { message: string | null } = { message: null };

  static getDerivedStateFromError(error: Error): { message: string } {
    return { message: error.message || "page failed" };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("page.crash", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.message) {
      return (
        <div className="p-8">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-teal">page</div>
          <h1 className="mt-2 text-lg font-medium">This page crashed</h1>
          <p className="mt-2 text-sm text-mute">{this.state.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function BootScreen({ message }: { message: string }) {
  return (
    <div className="flex min-h-full items-center justify-center">
      <div className="text-center">
        <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.22em] text-teal">CDIALER</div>
        <div className="text-sm text-mute">{message}</div>
      </div>
    </div>
  );
}

function Gate({ children }: { children: ReactNode }) {
  const { state } = useStore();
  const location = useLocation();
  if (state.boot === "booting") return <BootScreen message="Connecting…" />;
  if (state.boot === "error") return <BootScreen message={state.bootError ?? "Unreachable"} />;
  const onSetup = location.pathname === "/setup";
  const onLogin = location.pathname === "/login";
  if (!state.setup && !onSetup) return <Navigate to="/setup" replace />;
  if (state.setup && onSetup) {
    return <Navigate to={state.authenticated ? "/console" : "/login"} replace />;
  }
  if (state.setup && !state.authenticated && !onLogin) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

/**
 * Authenticated chrome. Console stays mounted (hidden) so the live surface never unmounts.
 */
function Shell() {
  const location = useLocation();
  const onConsole = location.pathname === "/console";
  return (
    <div className="flex h-full min-h-0">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar />
        <div className="flex min-h-0 flex-1 flex-col">
          <div className={onConsole ? "flex min-h-0 flex-1 flex-col" : "hidden"}>
            <Console active={onConsole} />
          </div>
          {!onConsole ? (
            <PageCrash>
              <Outlet />
            </PageCrash>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * CDIALER operator SPA.
 */
export default function App() {
  return (
    <Gate>
      <ToastHost />
      <Routes>
        <Route path="/setup" element={<Setup />} />
        <Route path="/login" element={<Login />} />
        <Route element={<Shell />}>
          <Route path="/" element={<Navigate to="/console" replace />} />
          <Route path="/console" element={null} />
          <Route path="/call" element={<CallSetup />} />
          <Route path="/leads" element={<Navigate to="/console" replace />} />
          <Route path="/history" element={<History />} />
          <Route path="/stats" element={<Stats />} />
          <Route path="/dashboard" element={<Navigate to="/stats" replace />} />
          <Route path="/logs" element={<Logs />} />
          <Route path="/ivr" element={<Navigate to="/scripts" replace />} />
          {featureRoutes().map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
          <Route path="/sip" element={<Sip />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/admin" element={<Admin />} />
        </Route>
        <Route path="*" element={<Navigate to="/console" replace />} />
      </Routes>
    </Gate>
  );
}
